import { useEffect, useState, useCallback } from 'react';
import Navbar from '../components/Navbar.jsx';
import api from '../lib/api.js';

// MAPTRIUM Hub control page.
// Replaces the old AdminDesktop operator console. Backend is the Hub Portal
// Container App; URL is kept out of the user's muscle memory by using the
// helper in src/lib/api.js (hubGet/hubPost). Browser only ever sees
// maptrium.com/admin/desktop; the Container App URL stays invisible.

const TABS = ['licenses', 'testers', 'installs', 'deliveries', 'team', 'audit'];
const HUB_KEY_LS = 'maptrium.hub.admin_key';

function daysUntil(ts) {
  if (!ts) return null;
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}
function fmtDate(ts) {
  if (!ts) return '—';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}
function toast(msg, isErr = false) {
  const el = document.getElementById('hub_toast');
  if (!el) return;
  el.textContent = msg;
  el.className = `fixed bottom-6 right-6 px-4 py-3 rounded shadow-lg text-sm z-50 ${isErr ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-emerald-100 text-emerald-800 border border-emerald-300'}`;
  el.style.display = 'block';
  setTimeout(() => { el.style.display = 'none'; }, 3500);
}

export default function AdminHub() {
  const [hubKey, setHubKey] = useState(() => localStorage.getItem(HUB_KEY_LS) || '');
  const [keyInput, setKeyInput] = useState('');
  const [tab, setTab] = useState('licenses');
  const [state, setState] = useState({
    licenses: [], tiers: [], admins: [], deliveries: [], installs: [], audit: [],
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showIssue, setShowIssue] = useState(null); // 'trainer' | 'desktop_individual' | null
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showDelivery, setShowDelivery] = useState(false);
  const [reveal, setReveal] = useState(null); // text to show after issuing license/admin

  const refresh = useCallback(async () => {
    if (!hubKey) return;
    setLoading(true); setErr('');
    try {
      const [lic, tier, adm, del, ins, aud] = await Promise.all([
        api.hubGet('/api/licenses', hubKey),
        api.hubGet('/api/tiers', hubKey),
        api.hubGet('/api/admins', hubKey),
        api.hubGet('/api/deliveries?limit=100', hubKey),
        api.hubGet('/api/installs', hubKey),
        api.hubGet('/api/audit?limit=200', hubKey),
      ]);
      setState({
        licenses: lic.licenses || lic || [],
        tiers: tier.tiers || tier || [],
        admins: adm.admins || adm || [],
        deliveries: del.deliveries || del || [],
        installs: ins.installs || ins || [],
        audit: aud.audit || aud || [],
      });
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        setHubKey(''); localStorage.removeItem(HUB_KEY_LS);
        setErr('Hub admin key invalid or expired. Enter again.');
      } else {
        setErr(e.message || String(e));
      }
    } finally { setLoading(false); }
  }, [hubKey]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!hubKey) return;
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [hubKey, refresh]);

  // -------- key gate --------
  if (!hubKey) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="max-w-md mx-auto px-6 py-20">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h1 className="text-xl font-bold text-navy font-alata mb-2">MAPTRIUM Hub</h1>
            <p className="text-sm text-ink-muted mb-4">
              Enter your Hub admin key. One time per browser; stored locally so you don't retype on every visit.
            </p>
            <input
              type="password"
              className="w-full px-3 py-2 border border-slate-300 rounded font-mono text-sm"
              placeholder="X-Admin-Key"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && keyInput.trim()) { localStorage.setItem(HUB_KEY_LS, keyInput.trim()); setHubKey(keyInput.trim()); } }}
              autoFocus
            />
            <button
              className="mt-3 w-full px-4 py-2 bg-gold text-navy rounded font-semibold text-sm hover:brightness-95"
              style={{ backgroundColor: '#DBC078', color: '#032953' }}
              onClick={() => { if (keyInput.trim()) { localStorage.setItem(HUB_KEY_LS, keyInput.trim()); setHubKey(keyInput.trim()); } }}
            >
              Enter
            </button>
            {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
            <div className="mt-6 pt-4 border-t text-xs text-ink-muted">
              Lost it? SSH to a workstation with Postgres access and{' '}
              <code className="bg-slate-100 px-1 rounded">SELECT admin_key FROM admin_users WHERE id='admin_master';</code>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // -------- derived stats --------
  const activeLicenses = state.licenses.filter(l => !l.revoked);
  const testers = activeLicenses.filter(l => (l.tier_id || '').includes('trainer') || l.edition === 'trainer');
  const now = Date.now() / 1000;
  const expiring = activeLicenses.filter(l => l.exp && l.exp > now && l.exp < now + 7 * 86400);

  // -------- mutations --------
  async function issueLicense(payload) {
    try {
      const r = await api.hubPost('/api/licenses/from-tier', hubKey, payload);
      setReveal(`License issued: ${r.license_id || JSON.stringify(r)}`);
      toast('License issued');
      refresh();
    } catch (e) { toast('Issue failed: ' + e.message, true); }
  }
  async function revokeLicense(lid) {
    if (!confirm(`Revoke license ${lid}? Customer loses access on next heartbeat.`)) return;
    const reason = prompt('Reason (shown in audit log):', 'pilot ended');
    try { await api.hubPost(`/api/licenses/${lid}/revoke`, hubKey, { reason: reason || '' }); toast('Revoked'); refresh(); }
    catch (e) { toast('Revoke failed: ' + e.message, true); }
  }
  async function rotateLicense(lid) {
    if (!confirm(`Rotate ${lid}? Current token invalidated, new one issued.`)) return;
    try { await api.hubPost(`/api/licenses/${lid}/rotate`, hubKey, {}); toast('Rotated'); refresh(); }
    catch (e) { toast('Rotate failed: ' + e.message, true); }
  }
  async function renewTester(lid) {
    try { await api.hubPost(`/api/licenses/${lid}/rotate`, hubKey, { extend_days: 60 }); toast('Renewed +60d'); refresh(); }
    catch (e) { toast('Renew failed: ' + e.message, true); }
  }
  async function addAdmin(payload) {
    try {
      const r = await api.hubPost('/api/admins', hubKey, payload);
      setReveal(`Admin created. Key for ${payload.email}: ${r.admin_key || '(fetch from DB)'} — share via secure channel; not shown again.`);
      toast('Admin created');
      refresh();
    } catch (e) { toast('Create failed: ' + e.message, true); }
  }
  async function deactivateAdmin(aid) {
    if (!confirm(`Deactivate ${aid}? Their key stops working immediately.`)) return;
    try { await api.hubPost(`/api/admins/${aid}/deactivate`, hubKey, {}); toast('Deactivated'); refresh(); }
    catch (e) { toast('Deactivate failed: ' + e.message, true); }
  }
  async function recordDelivery(payload) {
    try { await api.hubPost('/api/deliveries', hubKey, payload); toast('Delivery recorded'); refresh(); }
    catch (e) { toast('Record failed: ' + e.message, true); }
  }
  function logout() {
    if (!confirm('Forget Hub admin key on this browser?')) return;
    localStorage.removeItem(HUB_KEY_LS); setHubKey(''); setKeyInput('');
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-navy font-alata">MAPTRIUM&trade; Hub</h1>
            <p className="text-sm text-ink-muted">License + ML sync control for Desktop, Web, and Defense MAPTRIUM</p>
          </div>
          <div className="flex gap-2">
            <button onClick={refresh} className="px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-100" disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button onClick={logout} className="px-3 py-1.5 border border-slate-300 rounded text-sm text-ink-muted hover:bg-slate-100">
              Forget key
            </button>
          </div>
        </div>

        {err && <div className="mb-4 bg-red-50 border border-red-200 text-red-800 text-sm p-3 rounded">{err}</div>}

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <StatCard label="Active licenses" value={activeLicenses.length} tone="emerald" />
          <StatCard label="Testers" value={testers.length} sub={`${state.installs.length} installs`} tone="gold" />
          <StatCard label="Expiring <7d" value={expiring.length} sub="renewals needed" tone="amber" />
          <StatCard label="Admins" value={state.admins.filter(a => a.active).length} sub="team members" tone="emerald" />
          <StatCard label="Audit events" value={state.audit.length} sub="last 200" tone="slate" />
        </div>

        {/* Primary CTAs */}
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            className="px-4 py-2 rounded font-semibold text-sm text-navy shadow"
            style={{ backgroundColor: '#DBC078' }}
            onClick={() => { setShowIssue('trainer'); setReveal(null); }}
          >
            + Issue Tester Bundle
          </button>
          <button
            className="px-4 py-2 border-2 border-navy text-navy rounded font-semibold text-sm hover:bg-navy hover:text-white"
            onClick={() => { setShowIssue('desktop_individual'); setReveal(null); }}
          >
            + Issue Customer License
          </button>
          <button
            className="px-4 py-2 border border-slate-300 text-ink rounded text-sm hover:bg-slate-100"
            onClick={() => setShowDelivery(true)}
          >
            + Record Delivery
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-4 flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border-b-2 ${tab === t ? 'border-gold text-navy' : 'border-transparent text-ink-muted hover:text-navy'}`}
            >
              {t === 'licenses' ? 'Licenses' : t === 'testers' ? 'Testers' : t === 'installs' ? 'Installs' : t === 'deliveries' ? 'Deliveries' : t === 'team' ? 'Team' : 'Audit'}
            </button>
          ))}
        </div>

        {tab === 'licenses' && <LicensesTab licenses={state.licenses} onRevoke={revokeLicense} onRotate={rotateLicense} />}
        {tab === 'testers' && <TestersTab testers={testers} installs={state.installs} onRenew={renewTester} onRevoke={revokeLicense} />}
        {tab === 'installs' && <InstallsTab installs={state.installs} />}
        {tab === 'deliveries' && <DeliveriesTab deliveries={state.deliveries} />}
        {tab === 'team' && <TeamTab admins={state.admins} onAdd={() => setShowAddAdmin(true)} onDeactivate={deactivateAdmin} />}
        {tab === 'audit' && <AuditTab audit={state.audit} />}

        {/* Modals */}
        {showIssue && (
          <IssueModal
            defaultTier={showIssue}
            tiers={state.tiers}
            reveal={reveal}
            onClose={() => { setShowIssue(null); setReveal(null); }}
            onSubmit={issueLicense}
          />
        )}
        {showAddAdmin && (
          <AddAdminModal
            reveal={reveal}
            onClose={() => { setShowAddAdmin(false); setReveal(null); }}
            onSubmit={addAdmin}
          />
        )}
        {showDelivery && (
          <DeliveryModal
            licenses={activeLicenses}
            onClose={() => setShowDelivery(false)}
            onSubmit={(p) => { recordDelivery(p); setShowDelivery(false); }}
          />
        )}

        <div id="hub_toast" style={{ display: 'none' }} />

        {/* Help footer */}
        <div className="mt-10 pt-4 border-t border-slate-200 text-xs text-ink-muted">
          <strong className="text-navy">What this page does:</strong> issues PASETO v4 licenses for Desktop/Web/Defense MAPTRIUM, tracks installs + corrections synced from testers' Hub protocol, manages your admin team.{' '}
          Backend is the MAPTRIUM Hub Container App — URL is intentionally hidden; you never need it.{' '}
          Docs: <code className="bg-slate-100 px-1 rounded">docs/CONTROL_PLANE_ARCHITECTURE.md</code>
        </div>
      </main>
    </div>
  );
}

// ---------- Components ----------

function StatCard({ label, value, sub, tone }) {
  const colors = {
    emerald: 'text-emerald-600',
    gold:    'text-amber-600',
    amber:   'text-amber-700',
    slate:   'text-slate-700',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${colors[tone] || 'text-navy'}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function Pill({ tone, children }) {
  const map = {
    ok:   'bg-emerald-100 text-emerald-800 border-emerald-200',
    warn: 'bg-amber-100 text-amber-800 border-amber-200',
    err:  'bg-red-100 text-red-800 border-red-200',
    navy: 'bg-slate-100 text-navy border-slate-200',
    gold: 'text-navy border-amber-300',
  };
  const style = tone === 'gold' ? { backgroundColor: '#DBC078' } : {};
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${map[tone] || map.navy}`} style={style}>{children}</span>;
}

function Button({ onClick, danger, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 text-[11px] border rounded mr-1 ${danger ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}
    >{children}</button>
  );
}

function LicensesTab({ licenses, onRevoke, onRotate }) {
  const [q, setQ] = useState(''); const [filter, setFilter] = useState('');
  let rows = licenses;
  if (q) rows = rows.filter(l => Object.values(l).some(v => String(v || '').toLowerCase().includes(q.toLowerCase())));
  if (filter === 'active')   rows = rows.filter(l => !l.revoked);
  if (filter === 'revoked')  rows = rows.filter(l => l.revoked);
  if (filter === 'expiring') rows = rows.filter(l => { const d = daysUntil(l.exp); return d !== null && d > 0 && d < 30; });

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex gap-2 mb-3">
        <input className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" placeholder="Search by license ID, customer, or email…" value={q} onChange={e => setQ(e.target.value)} />
        <select className="px-3 py-2 border border-slate-300 rounded text-sm" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active only</option>
          <option value="revoked">Revoked only</option>
          <option value="expiring">Expiring &lt; 30d</option>
        </select>
      </div>
      {!rows.length ? <div className="text-center py-10 text-sm text-ink-muted">No licenses match.</div> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-ink-muted">
              <th className="py-2 px-2">License</th><th>Customer</th><th>Tier</th><th>Expires</th><th>Devices</th><th>Status</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(l => {
              const dLeft = daysUntil(l.exp);
              const status = l.revoked ? <Pill tone="err">Revoked</Pill>
                : dLeft !== null && dLeft < 0 ? <Pill tone="err">Expired</Pill>
                : dLeft !== null && dLeft < 7 ? <Pill tone="warn">Expiring {dLeft}d</Pill>
                : <Pill tone="ok">Active</Pill>;
              return (
                <tr key={l.license_id || l.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-2 font-mono text-xs">{l.license_id || l.id}</td>
                  <td>{l.customer_id || '—'}</td>
                  <td><Pill tone="navy">{l.tier_id || l.edition || '—'}</Pill></td>
                  <td className="text-xs">{fmtDate(l.exp)}</td>
                  <td className="font-mono text-xs">{l.installs_count || 0} / {l.max_devices || '∞'}</td>
                  <td>{status}</td>
                  <td className="text-right">
                    {!l.revoked && <Button onClick={() => onRotate(l.license_id)}>Rotate</Button>}
                    {!l.revoked && <Button onClick={() => onRevoke(l.license_id)} danger>Revoke</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TestersTab({ testers, installs, onRenew, onRevoke }) {
  if (!testers.length) return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-sm text-ink-muted">
      No testers yet. Click <b className="text-navy">+ Issue Tester Bundle</b> at the top to add one.
    </div>
  );
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-ink-muted">
            <th className="py-2 px-2">Tester</th><th>Email</th><th>Expires</th><th>Install</th><th>Status</th><th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {testers.map(l => {
            const inst = installs.find(i => i.license_id === l.license_id);
            const dLeft = daysUntil(l.exp);
            const status = l.revoked ? <Pill tone="err">Revoked</Pill>
              : dLeft < 0 ? <Pill tone="err">Expired</Pill>
              : dLeft < 14 ? <Pill tone="warn">{dLeft}d left</Pill>
              : <Pill tone="ok">Active</Pill>;
            return (
              <tr key={l.license_id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-2">{l.customer_id || '—'}</td>
                <td className="font-mono text-xs">{l.contact_email || '—'}</td>
                <td className="text-xs">{fmtDate(l.exp)}</td>
                <td className="font-mono text-xs">{inst ? inst.install_id?.slice(0,10) + '…' : <span className="text-ink-muted italic">not activated</span>}</td>
                <td>{status}</td>
                <td className="text-right">
                  <Button onClick={() => onRenew(l.license_id)}>+60d</Button>
                  {!l.revoked && <Button onClick={() => onRevoke(l.license_id)} danger>Revoke</Button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function InstallsTab({ installs }) {
  if (!installs.length) return <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-sm text-ink-muted">No activations yet.</div>;
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <p className="text-xs text-ink-muted mb-3">Every physical PC that activated against a license. Hard-locked installs = PC-bound and non-copyable.</p>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-ink-muted"><th className="py-2 px-2">Install</th><th>License</th><th>Host</th><th>Activated</th><th>Last heartbeat</th></tr></thead>
        <tbody>
          {installs.map(i => (
            <tr key={i.install_id} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-2 px-2 font-mono text-xs">{(i.install_id || '').slice(0,16)}…</td>
              <td className="font-mono text-xs">{i.license_id || '—'}</td>
              <td className="font-mono text-xs">{i.hostname || '—'}</td>
              <td className="text-xs">{fmtDate(i.activated_at)}</td>
              <td className="text-xs">{fmtDate(i.last_heartbeat)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeliveriesTab({ deliveries }) {
  if (!deliveries.length) return <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-sm text-ink-muted">No deliveries recorded.</div>;
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-ink-muted"><th className="py-2 px-2">When</th><th>License</th><th>Method</th><th>Sent to</th><th>By</th></tr></thead>
        <tbody>
          {deliveries.map((d, i) => (
            <tr key={d.id || i} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-2 px-2 text-xs">{fmtDate(d.delivered_at || d.created_at)}</td>
              <td className="font-mono text-xs">{d.license_id || '—'}</td>
              <td><Pill tone="navy">{d.method || '—'}</Pill></td>
              <td className="font-mono text-xs">{d.sent_to || '—'}</td>
              <td className="font-mono text-xs">{d.delivered_by || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamTab({ admins, onAdd, onDeactivate }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <button className="mb-3 px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-100" onClick={onAdd}>+ Add admin</button>
      {!admins.length ? <div className="text-center py-6 text-sm text-ink-muted">No admins yet.</div> : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-ink-muted"><th className="py-2 px-2">Name</th><th>Email</th><th>Role</th><th>Last seen</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {admins.map(a => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-2">{a.name || '—'}</td>
                <td className="font-mono text-xs">{a.email || '—'}</td>
                <td><Pill tone={a.role === 'owner' ? 'gold' : 'navy'}>{a.role || '—'}</Pill></td>
                <td className="text-xs">{fmtDate(a.last_seen)}</td>
                <td>{a.active ? <Pill tone="ok">Active</Pill> : <Pill tone="err">Inactive</Pill>}</td>
                <td className="text-right">
                  {a.active && a.id !== 'admin_master' && <Button onClick={() => onDeactivate(a.id)} danger>Deactivate</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AuditTab({ audit }) {
  if (!audit.length) return <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-sm text-ink-muted">Audit empty.</div>;
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-ink-muted"><th className="py-2 px-2">When</th><th>Actor</th><th>Event</th><th>Details</th></tr></thead>
        <tbody>
          {audit.map(e => (
            <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-2 px-2 text-xs">{fmtDate(e.ts)}</td>
              <td className="font-mono text-xs">{e.actor || '—'}</td>
              <td><Pill tone="navy">{e.event || '—'}</Pill></td>
              <td className="font-mono text-[10px] text-ink-muted">{(e.details_json || '').slice(0,100)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------- Modals --------

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-navy font-alata mb-3">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function IssueModal({ defaultTier, tiers, reveal, onClose, onSubmit }) {
  const [tier, setTier] = useState(defaultTier);
  const [cust, setCust] = useState('');
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(defaultTier === 'trainer' ? 60 : 365);
  const [devices, setDevices] = useState(1);
  const [notes, setNotes] = useState('');

  return (
    <Modal title={defaultTier === 'trainer' ? 'Issue Tester Bundle' : 'Issue Customer License'} onClose={onClose}>
      <Field label="Tier">
        <select className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={tier} onChange={e => setTier(e.target.value)}>
          {tiers.map(t => {
            const id = t.id || t.tier_id || t.key;
            const price = t.price_usd === 0 ? ' (free)' : t.price_usd ? ` ($${t.price_usd}/${t.billing_period || 'yr'})` : ' (custom)';
            return <option key={id} value={id}>{(t.label || id) + price}</option>;
          })}
        </select>
      </Field>
      <Field label="Customer / Tester name"><input className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={cust} onChange={e => setCust(e.target.value)} placeholder="e.g., Alice Reviewer" /></Field>
      <Field label="Contact email"><input type="email" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={email} onChange={e => setEmail(e.target.value)} placeholder="alice@example.com" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Valid days"><input type="number" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={days} onChange={e => setDays(e.target.value)} /></Field>
        <Field label="Max devices"><input type="number" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={devices} onChange={e => setDevices(e.target.value)} /></Field>
      </div>
      <Field label="Notes (optional)"><textarea className="w-full px-3 py-2 border border-slate-300 rounded text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></Field>
      {reveal && <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs font-mono text-emerald-800 break-all">{reveal}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button className="px-4 py-2 border border-slate-300 rounded text-sm" onClick={onClose}>Close</button>
        <button
          className="px-4 py-2 rounded font-semibold text-sm text-navy"
          style={{ backgroundColor: '#DBC078' }}
          disabled={!cust || !email}
          onClick={() => onSubmit({ tier_id: tier, customer_id: cust, contact_email: email, valid_days: parseInt(days, 10) || 60, max_devices: parseInt(devices, 10) || 1, notes })}
        >Issue + Reveal</button>
      </div>
    </Modal>
  );
}

function AddAdminModal({ reveal, onClose, onSubmit }) {
  const [name, setName] = useState(''); const [email, setEmail] = useState(''); const [role, setRole] = useState('admin');
  return (
    <Modal title="Add admin team member" onClose={onClose}>
      <Field label="Full name"><input className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label="Email"><input type="email" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={email} onChange={e => setEmail(e.target.value)} /></Field>
      <Field label="Role">
        <select className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={role} onChange={e => setRole(e.target.value)}>
          <option value="owner">Owner (all actions)</option>
          <option value="admin">Admin (issue + revoke)</option>
          <option value="support">Support (read-only)</option>
        </select>
      </Field>
      {reveal && <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs font-mono text-emerald-800 break-all">{reveal}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button className="px-4 py-2 border border-slate-300 rounded text-sm" onClick={onClose}>Close</button>
        <button
          className="px-4 py-2 rounded font-semibold text-sm text-navy"
          style={{ backgroundColor: '#DBC078' }}
          disabled={!name || !email}
          onClick={() => onSubmit({ name, email, role })}
        >Create + Reveal Key</button>
      </div>
    </Modal>
  );
}

function DeliveryModal({ licenses, onClose, onSubmit }) {
  const [license_id, setLid] = useState(licenses[0]?.license_id || '');
  const [method, setMethod] = useState('email');
  const [sent_to, setTo] = useState('');
  const [link, setLink] = useState('');
  return (
    <Modal title="Record delivery" onClose={onClose}>
      <Field label="License">
        <select className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={license_id} onChange={e => setLid(e.target.value)}>
          {licenses.map(l => <option key={l.license_id} value={l.license_id}>{l.license_id} — {l.customer_id || '(no name)'}</option>)}
        </select>
      </Field>
      <Field label="Delivery method">
        <select className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={method} onChange={e => setMethod(e.target.value)}>
          <option value="email">Email</option>
          <option value="onedrive_link">OneDrive link</option>
          <option value="dropbox_link">Dropbox link</option>
          <option value="wetransfer">WeTransfer</option>
          <option value="encrypted_usb">Encrypted USB (courier)</option>
          <option value="other">Other</option>
        </select>
      </Field>
      <Field label="Recipient"><input className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={sent_to} onChange={e => setTo(e.target.value)} placeholder="alice@example.com" /></Field>
      <Field label="Link / tracking (optional)"><input className="w-full px-3 py-2 border border-slate-300 rounded text-sm" value={link} onChange={e => setLink(e.target.value)} /></Field>
      <div className="flex justify-end gap-2 mt-4">
        <button className="px-4 py-2 border border-slate-300 rounded text-sm" onClick={onClose}>Cancel</button>
        <button
          className="px-4 py-2 rounded font-semibold text-sm text-navy"
          style={{ backgroundColor: '#DBC078' }}
          onClick={() => onSubmit({ license_id, method, sent_to, link })}
        >Record</button>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-3">
      <label className="block text-[11px] uppercase tracking-widest text-ink-muted font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}
